/**
 * @fileoverview
 * Timezone dropdown utility for frontend
 * Features:
 *  - Full IANA timezone list
 *  - GMT offset with DST support
 *  - Remove duplicates by GMT offset
 *  - US-friendly offsets prioritized
 *  - Keep IANA format (e.g., Asia/Seoul)
 *  - Sorted by GMT offset
 *  - Searchable
 *  - Works in Vue/React
 */

import moment from 'moment-timezone'

// ------------------- 1. Friendly labels for US-focused timezones -------------------
const US_FRIENDLY_TIMEZONES = {
  'Pacific/Honolulu': 'Hawaii Time',
  'America/Anchorage': 'Alaska Time',
  'America/Los_Angeles': 'Pacific Time',
  'America/Denver': 'Mountain Time',
  'America/Phoenix': 'Arizona',
  'America/Chicago': 'Central Time',
  'America/New_York': 'Eastern Time',
  'America/Halifax': 'Atlantic Time',

  'Europe/London': 'UTC',

}

// ------------------- 2. Utility functions -------------------

// Get current timezone offset in minutes (DST-aware)
function getOffsetMinutes(timezone) {
  return moment.tz(timezone).utcOffset()
}

// Format offset to GMT±hh:mm
function formatOffset(minutes) {
  const sign = minutes >= 0 ? '+' : '-'
  const abs = Math.abs(minutes)
  const hours = String(Math.floor(abs / 60)).padStart(2, '0')
  const mins = String(abs % 60).padStart(2, '0')
  return `GMT${sign}${hours}:${mins}`
}

// Generate label combining GMT offset + friendly annotation (optional)
function getTimezoneLabel(tz) {
  const offset = getOffsetMinutes(tz)
  const friendly = US_FRIENDLY_TIMEZONES[tz]
  return friendly
    ? `(${formatOffset(offset)}) ${tz} — ${friendly}`
    : `(${formatOffset(offset)}) ${tz}`
}

// ------------------- 3. Build timezone options (unique by offset) -------------------
const ALL_TIMEZONES = moment.tz.names()
const uniqueOffsetMap = new Map()

ALL_TIMEZONES.forEach(tz => {
  const offset = getOffsetMinutes(tz)
  if (!uniqueOffsetMap.has(offset)) {
    uniqueOffsetMap.set(offset, { value: tz, offset, label: getTimezoneLabel(tz) })
  } else {
    // If existing, prioritize US-friendly timezone
    if (US_FRIENDLY_TIMEZONES[tz]) {
      uniqueOffsetMap.set(offset, { value: tz, offset, label: getTimezoneLabel(tz) })
    }
  }
})

// Convert map to array and sort by GMT offset
export const TIMEZONE_OPTIONS = Array.from(uniqueOffsetMap.values()).sort(
  (a, b) => a.offset - b.offset
)

export const SHORT_TIMEZONE_OPTIONS = TIMEZONE_OPTIONS.map(({ value, offset }) => ({
  value,
  offset,
  label: `(${formatOffset(offset)}) ${value}`
}))

const DEFAULT_TIMEZONE = 'America/New_York'

// Legacy US timezone abbreviations (e.g. the backend's historical "PST" default) mapped
// to a DST-aware IANA zone so consumers get correct year-round behavior.
const LEGACY_ABBREVIATION_TO_IANA = {
  HST: 'Pacific/Honolulu',
  AKST: 'America/Anchorage',
  AKDT: 'America/Anchorage',
  PST: 'America/Los_Angeles',
  PDT: 'America/Los_Angeles',
  MST: 'America/Denver',
  MDT: 'America/Denver',
  CST: 'America/Chicago',
  CDT: 'America/Chicago',
  EST: 'America/New_York',
  EDT: 'America/New_York',
}

// Build a normalized UTC offset key (e.g. "UTC-7", "UTC+5:30") from offset minutes.
const buildUtcOffsetKey = (minutes) => {
  const sign = minutes >= 0 ? '+' : '-'
  const abs = Math.abs(minutes)
  const hours = Math.floor(abs / 60)
  const mins = abs % 60
  return mins === 0 ? `UTC${sign}${hours}` : `UTC${sign}${hours}:${String(mins).padStart(2, '0')}`
}

// Curated US/Canada timezones for the settings dropdown, ordered west -> east.
// Friendly names are keyed by the IANA zone (NOT by offset), so they stay correct across
// DST: "America/Los_Angeles" is always "Pacific Time", whether it's PST or PDT today.
// The "(UTC±n)" suffix is derived from the live offset, so it reads "UTC-7" in summer and
// "UTC-8" in winter automatically — no separate summer/winter table needed.
const US_TIMEZONE_LIST = [
  { value: 'Pacific/Honolulu', name: 'Hawaii' },
  { value: 'America/Anchorage', name: 'Alaska' },
  { value: 'America/Los_Angeles', name: 'Pacific Time (US & Canada)' },
  { value: 'America/Phoenix', name: 'Arizona' },
  { value: 'America/Denver', name: 'Mountain Time (US & Canada)' },
  { value: 'America/Chicago', name: 'Central Time (US & Canada)' },
  { value: 'America/New_York', name: 'Eastern Time (US & Canada)' },
  { value: 'America/Halifax', name: 'Atlantic Time (Canada)' },
]

// Major international timezones, ordered by offset. Same DST-safe keying: the name is fixed
// to the IANA zone and the "(UTC±n)" suffix follows the live offset (so e.g. London reads
// UTC+0 in winter, UTC+1 in summer; Sydney UTC+10 / UTC+11).
const WORLD_TIMEZONE_LIST = [
  { value: 'Pacific/Midway', name: 'Midway Island, Samoa' },
  { value: 'America/Sao_Paulo', name: 'Brasilia' },
  { value: 'America/Argentina/Buenos_Aires', name: 'Buenos Aires' },
  { value: 'America/St_Johns', name: 'Newfoundland' },
  { value: 'Atlantic/South_Georgia', name: 'Mid-Atlantic' },
  { value: 'Atlantic/Azores', name: 'Azores' },
  { value: 'Etc/UTC', name: 'UTC' },
  { value: 'Europe/London', name: 'London, Dublin, Lisbon' },
  { value: 'Europe/Paris', name: 'Amsterdam, Berlin, Madrid, Paris, Rome' },
  { value: 'Europe/Athens', name: 'Athens, Bucharest, Istanbul' },
  { value: 'Europe/Moscow', name: 'Moscow, St. Petersburg' },
  { value: 'Asia/Tehran', name: 'Tehran' },
  { value: 'Asia/Dubai', name: 'Abu Dhabi, Muscat' },
  { value: 'Asia/Kabul', name: 'Kabul' },
  { value: 'Asia/Karachi', name: 'Islamabad, Karachi' },
  { value: 'Asia/Kolkata', name: 'Chennai, Kolkata, Mumbai, New Delhi' },
  { value: 'Asia/Kathmandu', name: 'Kathmandu' },
  { value: 'Asia/Dhaka', name: 'Almaty, Dhaka' },
  { value: 'Asia/Yangon', name: 'Yangon (Rangoon)' },
  { value: 'Asia/Bangkok', name: 'Bangkok, Hanoi, Jakarta' },
  { value: 'Asia/Shanghai', name: 'Beijing, Hong Kong, Shanghai' },
  { value: 'Asia/Seoul', name: 'Seoul' },
  { value: 'Asia/Tokyo', name: 'Osaka, Sapporo, Tokyo' },
  { value: 'Australia/Adelaide', name: 'Adelaide' },
  { value: 'Australia/Sydney', name: 'Canberra, Melbourne, Sydney' },
  { value: 'Pacific/Guadalcanal', name: 'Solomon Islands, New Caledonia' },
  { value: 'Pacific/Auckland', name: 'Auckland, Wellington' },
  { value: 'Pacific/Tongatapu', name: "Nuku'alofa" },
]

const FRIENDLY_TIMEZONE_LIST = [...US_TIMEZONE_LIST, ...WORLD_TIMEZONE_LIST]

const FRIENDLY_NAME_BY_IANA = FRIENDLY_TIMEZONE_LIST.reduce((acc, { value, name }) => {
  acc[value] = name
  return acc
}, {})

// Friendly label for an IANA timezone, e.g. "Pacific Time (US & Canada) (UTC-7)".
// The name comes from the zone identity (DST-safe); the offset is the live one.
// Falls back to "IANA (UTC±n)" for zones without a friendly name.
function getFriendlyTimezoneLabel(tz) {
  const offsetKey = buildUtcOffsetKey(getOffsetMinutes(tz))
  const friendly = FRIENDLY_NAME_BY_IANA[tz]
  return friendly ? `${friendly} (${offsetKey})` : `${tz} (${offsetKey})`
}

// Dropdown options: real IANA values + the friendly "name (UTC±n)" label.
export const FRIENDLY_TIMEZONE_OPTIONS = FRIENDLY_TIMEZONE_LIST.map(({ value }) => ({
  value,
  offset: getOffsetMinutes(value),
  label: getFriendlyTimezoneLabel(value),
}))

const parseUtcOffsetValue = (value) => {
  const match = String(value || '').trim().match(/^UTC([+-])(\d{1,2})(?::(\d{2}))?$/i)
  if (!match) return null
  const [, sign, hours, minutes = '00'] = match
  const totalMinutes = (Number(hours) * 60) + Number(minutes)
  return sign === '+' ? totalMinutes : -totalMinutes
}

// ------------------- 4. Browser timezone helper -------------------
export function getBrowserTimezone() {
  return moment.tz.guess()
}

// ------------------- 5. Normalize timezone value -------------------
export function normalizeTimezoneValue(value) {
  if (!value) return value
  const exists = TIMEZONE_OPTIONS.find(t => t.value === value)
  if (exists) return exists.value

  // Preserve any valid IANA zone (e.g. "America/Los_Angeles"). TIMEZONE_OPTIONS is
  // deduped by current offset, so a valid zone is often absent from it — but the value
  // is handed straight to moment.tz() by every calendar consumer, which needs the real
  // zone (with its own DST rules), not a different zone that merely shares today's offset.
  if (moment.tz.zone(value)) return value

  // Legacy US abbreviations (e.g. the backend's "PST" default) -> DST-aware IANA zone.
  const abbreviation = LEGACY_ABBREVIATION_TO_IANA[String(value).trim().toUpperCase()]
  if (abbreviation) return abbreviation

  // Legacy / non-IANA values (e.g. "UTC-8"): resolve to a zone by offset.
  const utcOffset = parseUtcOffsetValue(value)
  if (utcOffset !== null) {
    const utcMatch = TIMEZONE_OPTIONS.find(t => t.offset === utcOffset)
    return utcMatch ? utcMatch.value : DEFAULT_TIMEZONE
  }

  const offset = getOffsetMinutes(value)
  const match = TIMEZONE_OPTIONS.find(t => t.offset === offset)
  return match ? match.value : DEFAULT_TIMEZONE
}

// Return the dropdown options, guaranteeing the stored zone is present so it can be
// selected and saved VERBATIM. The curated list doesn't cover every IANA zone, so a stored
// zone like "America/Detroit" may be absent — prepend it as its own option rather than
// coercing it to a same-offset curated zone, which would let a later save overwrite the
// server's original zone. Legacy values ("PST", "UTC-8") are first resolved to IANA.
export function getTimezoneOptionsWithValue(value, options = FRIENDLY_TIMEZONE_OPTIONS) {
  const iana = normalizeTimezoneValue(value)
  if (!iana || options.some(o => o.value === iana)) return options
  return [
    { value: iana, offset: getOffsetMinutes(iana), label: getTimezoneDisplayLabel(iana) },
    ...options,
  ]
}

// ------------------- 6. Display label converter -------------------
// Convert a stored timezone value (IANA like "America/Los_Angeles", or a legacy
// value like "PST" / "UTC-8") into a human-friendly display label, e.g.
// "Pacific Time (US & Canada) (UTC-8)".
export function getTimezoneDisplayLabel(value) {
  const iana = normalizeTimezoneValue(value) || DEFAULT_TIMEZONE
  return getFriendlyTimezoneLabel(iana)
}

// ------------------- 7. Search helper -------------------
export function searchTimezones(keyword) {
  if (!keyword) return TIMEZONE_OPTIONS
  const lower = keyword.toLowerCase()
  return TIMEZONE_OPTIONS.filter(
    t => t.label.toLowerCase().includes(lower) || t.value.toLowerCase().includes(lower)
  )
}